import type { StreamEvent } from '@vibeshell/shared';
import { getLocale, t } from './i18n';

// A deliberate gateway refusal (the §maintenance private-preview gate, the
// 402 credit wall) ships its own friendly message in the response body —
// always English on the wire. English UIs show it verbatim (the server copy
// is the source of truth and may carry fresher wording); non-English locales
// prefer the catalog twin so a Hebrew user isn't refused in English; a
// missing/unreadable body falls back to the catalog, which itself falls back
// to English. True network failures never reach this helper — they keep the
// generic connection-error copy via the onError path.
function refusalMessage(serverMessage: string, catalogKey: string): string {
  if (getLocale() !== 'en') return t(catalogKey);
  return serverMessage || t(catalogKey);
}

export interface SseHandler {
  onEvent: (event: StreamEvent) => void;
  onClose?: () => void;
  onError?: (e: unknown) => void;
  /** Raised on a 403 GUEST_LIMIT_REACHED — the freemium auth wall. */
  onAuthWall?: () => void;
  /**
   * Out-of-band callback for the generation-worker's `app_name` event —
   * the friendly human name the model picks for the new app, emitted ahead
   * of `deploy_complete` so the GenerationStream can swap its summary into
   * the real name as soon as it arrives. The event is recognized here
   * BEFORE the parsed JSON is narrowed to the strict `StreamEvent` union
   * (which lives in @vibeshell/shared and is intentionally not extended
   * from the creator app).
   */
  onAppName?: (name: string) => void;
  /**
   * §A4 — out-of-band callback for the worker's `queued` event (the GenGate
   * herd-smoother). Under a viral spike the gate holds a gen in a FIFO queue
   * and streams position/ETA frames; this surfaces the HONEST "you're in
   * line, ~Xs" message instead of a silent stall on the synthesis animation.
   * Sidecar like app_name — not part of the shared StreamEvent union.
   */
  onQueued?: (position: number, etaSeconds: number, queueLength: number) => void;
}

export type GenerationMode = 'quick' | 'deep';

export interface GenerateRequestBody {
  prompt: string;
  deviceId: string;
  sessionId: string;
  mode?: GenerationMode;
  /**
   * When set, this generation runs in revise mode — the server takes the
   * previous JSX + theme as context and produces a patched output. The
   * worker dispatches to the revise system prompt instead of the
   * cold-start synthesis prompt.
   */
  revise?: {
    basedOnAppId: string;
    previousJsx: string;
    previousTheme: import('@vibeshell/shared').ThemeManifest;
    previousPrompt: string;
  };
}

// Phase 9 — API base defaults to '' (same-origin) so /v1/generate is
// resolved against the creator's host. The Cloudflare Pages Function at
// functions/v1/[[path]].ts proxies these calls into the api-gateway
// worker server-side, which means every Set-Cookie from the gateway
// lands as a FIRST-PARTY cookie on pomela.app and survives iOS Safari
// ITP. Override at build time via VITE_API_BASE (e.g. '/api' for the
// classic dev-server proxy, or an explicit https origin for a staging
// rig that wants to skip the Pages relay).
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

// EventSource doesn't support POST bodies, so we hand-roll a fetch + reader
// loop over the chunked text/event-stream response. Same wire format, same
// reconnect semantics we'd write anyway.
export async function streamGeneration(
  body: GenerateRequestBody,
  handler: SseHandler
): Promise<AbortController> {
  const abort = new AbortController();

  const url = `${API_BASE}/v1/generate`;
  void (async () => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        // credentials: 'include' is retained for parity with the dev
        // proxy path (where it's effectively a no-op on same-origin)
        // and for backward compatibility with a future setup where
        // VITE_API_BASE points at a different host. In the production
        // Phase 9 same-origin path it's redundant but harmless.
        credentials: 'include',
        body: JSON.stringify(body),
        signal: abort.signal
      });

      if (!res.ok || !res.body) {
        // A 429 is the Pomela rate limit — a terminal, non-retryable
        // outcome, NOT a dropped socket. Surface the playful message as
        // an `error` event then `stream_close` so RecoverableStream
        // settles instead of retrying into a "connection lost".
        if (res.status === 429) {
          let message =
            "You've hit your limit for now. Try again in a bit, or upgrade to Pro for more.";
          try {
            const body = (await res.json()) as { message?: string };
            if (body?.message) message = body.message;
          } catch {
            /* keep the default copy */
          }
          handler.onEvent({ type: 'error', message, retryable: false });
          handler.onEvent({ type: 'stream_close' });
          handler.onClose?.();
          return;
        }
        // A 403 GUEST_LIMIT_REACHED is the freemium auth wall — terminal
        // and non-retryable. Trip the auth modal AND fire an error event
        // with retryable:false so RecoverableStream marks the stream as
        // settled and the onClose below doesn't trigger handleFailure().
        //
        // Phase 9 fix: previously we fired onAuthWall + stream_close +
        // onClose. onClose ran into RecoverableStream.handleFailure
        // because no settling event preceded it, which fired ANOTHER
        // /v1/generate request, which 403'd again, which fired the
        // AuthWall AGAIN — the "auth wall flashing repeatedly" symptom.
        if (res.status === 403) {
          let code = '';
          let serverMessage = '';
          try {
            const body = (await res.json()) as { error?: string; message?: string };
            code = body?.error ?? '';
            if (typeof body?.message === 'string') serverMessage = body.message;
          } catch {
            /* unreadable body — handled below as an unexplained 403 */
          }
          if (code === 'GUEST_LIMIT_REACHED') {
            handler.onAuthWall?.();
            handler.onEvent({
              type: 'error',
              message:
                serverMessage || "You've hit the free generation limit. Sign in to keep building.",
              retryable: false
            });
            handler.onEvent({ type: 'stream_close' });
            handler.onClose?.();
            return;
          }
          // The §maintenance gate (GEN_MAINTENANCE='on') is a DELIBERATE,
          // friendly refusal — private preview, not an outage. Surface the
          // gateway's own message (or its localized twin) instead of the
          // misleading "couldn't reach the studio" network copy below.
          if (code === 'maintenance') {
            handler.onEvent({
              type: 'error',
              message: refusalMessage(serverMessage, 'err.maintenance'),
              retryable: false
            });
            handler.onEvent({ type: 'stream_close' });
            handler.onClose?.();
            return;
          }
          // Any OTHER 403 (CSRF guard, forbidden origin, device_owned)
          // is also terminal — there's no recovery path the user can
          // take from the same browser session. Settle the stream so
          // RecoverableStream doesn't burn retries on a 403 that won't
          // change. The state machine surfaces the error message.
          handler.onEvent({
            type: 'error',
            message: "We couldn't reach the studio just now. Refresh the page and try again.",
            retryable: false
          });
          handler.onEvent({ type: 'stream_close' });
          handler.onClose?.();
          return;
        }
        // A 402 is the credit wall (`credits_exhausted`) — like the
        // maintenance gate it's a deliberate refusal with friendly,
        // tier-aware copy minted by the gateway. Terminal and
        // non-retryable: surface that copy directly instead of wrapping
        // it in the generic "couldn't start your generation" phrasing.
        if (res.status === 402) {
          let serverMessage = '';
          try {
            const body = (await res.json()) as { message?: string };
            if (typeof body?.message === 'string') serverMessage = body.message;
          } catch {
            /* fall back to the localized copy */
          }
          handler.onEvent({
            type: 'error',
            message: refusalMessage(serverMessage, 'err.credits'),
            retryable: false
          });
          handler.onEvent({ type: 'stream_close' });
          handler.onClose?.();
          return;
        }
        // Any other 4xx (400 prompt_too_long, 401 unauthorized, 404,
        // 409 device_owned, …) is terminal at the network layer —
        // retrying won't change the outcome. Surface a non-retryable
        // error event so RecoverableStream settles. Phase 9 fix.
        if (res.status >= 400 && res.status < 500) {
          let detail = `HTTP ${res.status}`;
          try {
            const body = (await res.json()) as { error?: string; message?: string };
            if (body?.message) detail = body.message;
            else if (body?.error) detail = body.error;
          } catch {
            /* keep the HTTP-code fallback */
          }
          handler.onEvent({
            type: 'error',
            message: `We couldn't start your generation (${detail}).`,
            retryable: false
          });
          handler.onEvent({ type: 'stream_close' });
          handler.onClose?.();
          return;
        }
        // 5xx — genuinely transient. Let RecoverableStream do its
        // bounded retry dance via onError.
        handler.onError?.(new Error(`Generate failed: HTTP ${res.status}`));
        handler.onClose?.();
        return;
      }

      // Phase 9 — content-type defense. A 200 with `text/html` is the
      // smoking-gun signature of a Cloudflare Pages SPA fallback
      // intercepting an unproxied /v1/* request. Without this guard,
      // the reader loop below would silently consume the HTML, find
      // no `data: ` lines, exit cleanly, and RecoverableStream would
      // retry the same wrong route — burning the retry budget on a
      // misconfig that no amount of retry will fix.
      const contentType = res.headers.get('content-type') || '';
      if (!/text\/event-stream/i.test(contentType)) {
        handler.onEvent({
          type: 'error',
          message:
            'Something got tangled on our end and the studio sent back an ' +
            'unexpected response. Please reload in a moment.',
          retryable: false
        });
        handler.onEvent({ type: 'stream_close' });
        handler.onClose?.();
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIndex: number;
        while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          const json = dataLine.slice(6).trim();
          if (!json) continue;
          try {
            // Parse once into a permissive shape so we can recognise
            // out-of-union events (currently just `app_name`) before
            // narrowing to the strict StreamEvent.
            const parsed: unknown = JSON.parse(json);
            if (
              parsed &&
              typeof parsed === 'object' &&
              (parsed as { type?: unknown }).type === 'app_name'
            ) {
              const candidate = (parsed as { name?: unknown }).name;
              if (typeof candidate === 'string' && candidate.trim()) {
                handler.onAppName?.(candidate);
              }
              // app_name is a sidecar event — don't push it into onEvent
              // (the consumer's reducer is typed to StreamEvent and would
              // see an `unknown` shape otherwise).
              continue;
            }
            if (
              parsed &&
              typeof parsed === 'object' &&
              (parsed as { type?: unknown }).type === 'queued'
            ) {
              // §A4 sidecar — GenGate queue position/ETA (see SseHandler.onQueued).
              const q = parsed as { position?: unknown; etaSeconds?: unknown; queued?: unknown };
              handler.onQueued?.(
                typeof q.position === 'number' ? q.position : 1,
                typeof q.etaSeconds === 'number' ? q.etaSeconds : 0,
                typeof q.queued === 'number' ? q.queued : 0
              );
              continue;
            }
            const event = parsed as StreamEvent;
            handler.onEvent(event);
            if (event.type === 'stream_close') {
              handler.onClose?.();
              abort.abort();
              return;
            }
          } catch (e) {
            // Dev-only — keeps production consoles clean while still
            // surfacing malformed-event diagnostics in `vite dev`. Same
            // pattern analytics.ts uses for its PostHog payload trace.
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn('[sse] parse failed', e, json);
            }
          }
        }
      }
      handler.onClose?.();
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      handler.onError?.(e);
      handler.onClose?.();
    }
  })();

  return abort;
}
